// src/components/Admin/Settings/Integration_Setting/IntegrationMessages.jsx
import React, { useState, useEffect } from "react";
import {
  Edit2,
  Trash2,
  MessageSquare,
  Smartphone,
} from "lucide-react";

import useIntegration from "@/hooks/useIntegration";
import IntegrationFormModal from "./IntegrationModalForm";
import SmsTemplateModal from "./SmsTemplateModal";

export default function IntegrationMessages({ renderActions }) {
  const [selectedTab, setSelectedTab] = useState("whatsapp");

  // WhatsApp modal state
  const [editProvider, setEditProvider] = useState(null);
  const [showProviderModal, setShowProviderModal] = useState(false);

  // SMS modal state
  const [editingSmsItem, setEditingSmsItem] = useState(null);
  const [showSmsModal, setShowSmsModal] = useState(false);

  // WhatsApp providers from hook (API based)
  const { providers, loading, saveProvider, deleteProvider, toggleProviderActive, reload } = useIntegration();

  // SMS list – for now local state (you can later replace with API)
  const [smsRows, setSmsRows] = useState([]);
  const smsLoading = false;

  // Example events for SMS dropdown
  const smsEventOptions = [
    { value: "ORDER_CONFIRMED", label: "Order Confirmed" },
    { value: "BILL_GENERATED", label: "Bill Generated" },
    { value: "TABLE_BOOKED", label: "Table Booked" },
  ];

  // Actions for header
  const actions = {
    selectedTab,
    onAddTemplate: () => {
      setEditingSmsItem(null);
      setShowSmsModal(true);
    },
    onAddProvider: () => {
      setEditProvider(null);
      setShowProviderModal(true);
    },
    onRefresh: reload,
  };

  // Call renderActions if provided (for putting buttons in header)
  React.useEffect(() => {
    if (renderActions) {
      renderActions(actions);
    }
  }, [renderActions, selectedTab]);

  // Save WhatsApp provider (add / edit)
  const handleSaveProvider = async (payload) => {
    await saveProvider(editProvider?.id, payload);
    setShowProviderModal(false);
    setEditProvider(null);
  };

  // Save SMS template (local only – replace with API later)
  const handleSaveSmsTemplate = (data) => {
    if (editingSmsItem) {
      setSmsRows((prev) =>
        prev.map((row) =>
          row.id === editingSmsItem.id ? { ...row, ...data } : row
        )
      );
    } else {
      setSmsRows((prev) => [
        ...prev,
        { id: Date.now(), ...data },
      ]);
    }

    setShowSmsModal(false);
    setEditingSmsItem(null);
  };

  // RENDER SMS SECTION
  const renderSmsSection = () => (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wide">SR.No</th>
            <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wide">Template Name</th>
            <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wide">Message</th>
            <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wide">Event</th>
            <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wide">Route</th>
            <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wide">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {smsLoading ? (
            <tr>
              <td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-10 h-10 border-4 border-gray-200 border-t-teal-600 rounded-full animate-spin"></div>
                  <p className="text-sm font-medium">Loading...</p>
                </div>
              </td>
            </tr>
          ) : smsRows.length === 0 ? (
            <tr>
              <td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                    <MessageSquare className="w-8 h-8 text-gray-400" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">No SMS templates found</p>
                    <p className="text-sm text-gray-500 mt-1">Create your first SMS template</p>
                  </div>
                </div>
              </td>
            </tr>
          ) : (
            smsRows.map((row, i) => (
              <tr key={row.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm text-gray-700">{i + 1}</td>
                <td className="px-4 py-3 text-sm text-gray-800 font-medium">
                  {row.smsTemplate || row.templateName}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">{row.remarks}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{row.event}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{row.route || "—"}</td>
                <td className="px-4 py-3 text-center">
                  <div className="inline-flex items-center gap-1">
                    <button
                      className="p-1.5 text-gray-500 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                      onClick={() => {
                        setEditingSmsItem(row);
                        setShowSmsModal(true);
                      }}
                      title="Edit"
                    >
                      <Edit2 size={15} />
                    </button>
                    <button
                      className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      onClick={() => setSmsRows((prev) => prev.filter((r) => r.id !== row.id))}
                      title="Delete"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  // RENDER WHATSAPP SECTION
  const renderWhatsappSection = () => (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wide">SR.No</th>
            <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wide">Provider</th>
            <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wide">Instance Id</th>
            <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wide">Template</th>
            <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wide">Active</th>
            <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wide">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {loading ? (
            <tr>
              <td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-10 h-10 border-4 border-gray-200 border-t-teal-600 rounded-full animate-spin"></div>
                  <p className="text-sm font-medium">Loading...</p>
                </div>
              </td>
            </tr>
          ) : providers.length === 0 ? (
            <tr>
              <td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                    <Smartphone className="w-8 h-8 text-gray-400" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">No providers found</p>
                    <p className="text-sm text-gray-500 mt-1">Create your first WhatsApp provider</p>
                  </div>
                </div>
              </td>
            </tr>
          ) : (
            providers.map((row, i) => (
              <tr key={row.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm text-gray-700">{i + 1}</td>
                <td className="px-4 py-3 text-sm text-gray-800 font-medium">{row.provider}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{row.instanceId || "—"}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{row.templateName || "—"}</td>
                <td className="px-4 py-3 text-center">
                  <button
                    type="button"
                    onClick={() => toggleProviderActive(row)}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 ${
                      row.active ? "bg-teal-500" : "bg-gray-300"
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${
                        row.active ? "translate-x-4" : "translate-x-0.5"
                      }`}
                    />
                  </button>
                </td>
                <td className="px-4 py-3 text-center">
                  <div className="inline-flex items-center gap-1">
                    <button
                      onClick={() => {
                        setEditProvider(row);
                        setShowProviderModal(true);
                      }}
                      className="p-1.5 text-gray-500 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <Edit2 size={15} />
                    </button>
                    <button
                      onClick={() => deleteProvider(row.id)}
                      className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  // MAIN JSX
  return (
    <div className="min-h-full bg-gray-50 px-4 py-6">
      <div className="space-y-5">
        {/* Tabs */}
        <div className="flex gap-2">
          <button
            onClick={() => setSelectedTab("sms")}
            className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-all ${
              selectedTab === "sms"
                ? "bg-teal-600 text-white"
                : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
            }`}
          >
            <MessageSquare size={16} />
            SMS
          </button>
          <button
            onClick={() => setSelectedTab("whatsapp")}
            className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-all ${
              selectedTab === "whatsapp"
                ? "bg-teal-600 text-white"
                : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
            }`}
          >
            <Smartphone size={16} />
            WhatsApp
          </button>
        </div>

        {/* Content per tab */}
        {selectedTab === "sms" ? renderSmsSection() : renderWhatsappSection()}
      </div>

      {/* SMS Template Modal */}
      {showSmsModal && (
        <SmsTemplateModal
          item={editingSmsItem}
          eventOptions={smsEventOptions}
          onSave={handleSaveSmsTemplate}
          onClose={() => {
            setShowSmsModal(false);
            setEditingSmsItem(null);
          }}
        />
      )}

      {/* WhatsApp Provider Modal */}
      {showProviderModal && (
        <IntegrationFormModal
          item={editProvider}
          onSave={handleSaveProvider}
          onClose={() => {
            setShowProviderModal(false);
            setEditProvider(null);
          }}
        />
      )}
    </div>
  );
}
